import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { StatCard } from "../components/shared/StatCard";
import { BarChart } from "../components/shared/BarChart";
import { Spinner } from "../components/shared/Spinner";
import { reportsAPI } from "../services/api";
import { fmt, r1 } from "../components/shared/fmt";
import { useTitle } from "../hooks/useTitle";

export const ReportsPage: React.FC = () => {
	useTitle("Sales Reports");
	const [period, setPeriod] = useState<"week" | "month">("week");
	const { data: wData, isLoading: wl } = useQuery({
		queryKey: ["weekly"],
		queryFn: reportsAPI.weekly,
		retry: false,
	});
	const { data: mData } = useQuery({
		queryKey: ["monthly"],
		queryFn: () => reportsAPI.monthly(),
		retry: false,
	});
	const { data: iData } = useQuery({
		queryKey: ["ritems"],
		queryFn: reportsAPI.items,
		retry: false,
	});
	const weekly = wData?.data || [];
	const monthly = mData?.data || {};
	const items = iData?.data || [];

	if (wl) return <Spinner />;

	return (
		<div>
			<div className="page-title">Sales Reports & Analytics</div>
			<div className="page-sub">Performance insights and revenue breakdown</div>

			<div className="stat-grid">
				<StatCard
					label="Monthly Revenue"
					value={fmt(monthly.totalRevenue || 0)}
					cls="green"
					icon="💰"
					change="+7.2% vs last month"
					trend="up"
					accent="#06a384"
				/>
				<StatCard
					label="Monthly Orders"
					value={monthly.totalOrders || 0}
					icon="📋"
					change="+5.1%"
					trend="up"
				/>
				<StatCard
					label="Avg Order Value"
					value={fmt(monthly.avgOrder || 0)}
					cls="amber"
					icon="📊"
				/>
				<StatCard
					label="Total Tax"
					value={fmt(monthly.totalTax || 0)}
					cls="amber"
					icon="🏛️"
				/>
				<StatCard
					label="Total Discount"
					value={fmt(monthly.totalDiscount || 0)}
					cls="red"
					icon="🏷️"
				/>
			</div>

			<div className="grid-2">
				<div className="card">
					<div className="card-title">
						Revenue Trend
						<div className="filter-tabs" style={{ marginBottom: 0 }}>
							{(["week", "month"] as const).map((p) => (
								<button
									key={p}
									className={`filter-tab ${period === p ? "active" : ""}`}
									onClick={() => setPeriod(p)}>
									{p === "week" ? "This Week" : "This Year"}
								</button>
							))}
						</div>
					</div>
					{weekly.length > 0 ? (
						<BarChart
							data={weekly}
							valueKey="revenue"
							labelKey="day"
							color="#06a384"
						/>
					) : (
						<div
							style={{
								height: 120,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								color: "var(--muted)",
								fontSize: 13,
							}}>
							No data yet — create some orders first
						</div>
					)}
					{weekly.length > 0 && (
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginTop: 12,
								fontSize: 12,
								color: "var(--muted)",
							}}>
							<span>
								Week total:{" "}
								<strong className="text-accent">
									{fmt(weekly.reduce((s: number, d: any) => s + d.revenue, 0))}
								</strong>
							</span>
							<span>
								Avg/day:{" "}
								<strong className="text-amber">
									{fmt(
										weekly.reduce((s: number, d: any) => s + d.revenue, 0) / 7,
									)}
								</strong>
							</span>
						</div>
					)}
				</div>

				<div className="card">
					<div className="card-title">🏆 Top Performing Items</div>
					{items.slice(0, 8).map((item: any, i: number) => (
						<div
							key={item.id}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 10,
								padding: "7px 0",
								borderBottom: "1px solid rgba(26,43,29,.5)",
							}}>
							<span style={{ fontSize: 20, width: 28, textAlign: "center" }}>
								{item.emoji}
							</span>
							<div style={{ flex: 1 }}>
								<div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>
									{item.name}
								</div>
								<div
									style={{
										height: 4,
										width:
											items[0]?.revenue > 0
												? `${(item.revenue / items[0].revenue) * 100}%`
												: "4px",
										background: "linear-gradient(90deg,#06a384,#4ade80)",
										borderRadius: 99,
										minWidth: 4,
									}}
								/>
							</div>
							<div style={{ textAlign: "right", flexShrink: 0 }}>
								<div
									style={{
										fontSize: 12,
										fontWeight: 700,
										color: "var(--accent)",
									}}>
									{fmt(item.revenue)}
								</div>
								<div style={{ fontSize: 10, color: "var(--muted)" }}>
									{item.qty} sold
								</div>
							</div>
						</div>
					))}
					{items.length === 0 && (
						<div
							style={{
								color: "var(--muted)",
								fontSize: 13,
								padding: 20,
								textAlign: "center",
							}}>
							No sales data yet
						</div>
					)}
				</div>
			</div>

			{/* Category performance */}
			{items.length > 0 && (
				<div className="card">
					<div className="card-title">📊 Item Performance Detail</div>
					<table className="data-table">
						<thead>
							<tr>
								<th>Item</th>
								<th>Category</th>
								<th>Qty Sold</th>
								<th>Revenue</th>
								<th>COGS</th>
								<th>Gross Profit</th>
								<th>Margin</th>
							</tr>
						</thead>
						<tbody>
							{items.slice(0, 15).map((item: any) => (
								<tr key={item.id}>
									<td>
										<div
											style={{ display: "flex", alignItems: "center", gap: 8 }}>
											<span style={{ fontSize: 18 }}>{item.emoji}</span>
											<span style={{ fontWeight: 600 }}>{item.name}</span>
										</div>
									</td>
									<td>
										<span style={{ fontSize: 11, color: "var(--text2)" }}>
											{item.category}
										</span>
									</td>
									<td style={{ fontWeight: 700 }}>{item.qty}</td>
									<td className="text-accent" style={{ fontWeight: 700 }}>
										{fmt(item.revenue)}
									</td>
									<td style={{ color: "var(--red)" }}>{fmt(item.costTotal)}</td>
									<td
										style={{
											fontWeight: 700,
											color:
												item.grossProfit >= 0 ? "var(--accent)" : "var(--red)",
										}}>
										{fmt(item.grossProfit)}
									</td>
									<td>
										<span
											className={`tag ${item.margin > 60 ? "tag-green" : item.margin > 40 ? "tag-amber" : "tag-red"}`}>
											{r1(item.margin)}%
										</span>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};
